import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'token';
let authGeneration = 0;

// Keep the bearer API contract: converting every mutating endpoint to cookies
// would also require coordinated CSRF tokens. Tab-scoped storage limits
// persistence without changing those APIs.
localStorage.removeItem(TOKEN_KEY);

export const getAuthToken = () => sessionStorage.getItem(TOKEN_KEY);

export const setAuthToken = (token) => {
  authGeneration += 1;
  localStorage.removeItem(TOKEN_KEY);
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
};

export const clearAuthToken = (expectedGeneration) => {
  if (expectedGeneration !== undefined && expectedGeneration !== authGeneration) return false;
  authGeneration += 1;
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  return true;
};

export const clearAuthTokenForError = (error) => {
  const requestGeneration = error?.config?.authGeneration;
  return requestGeneration === undefined ? false : clearAuthToken(requestGeneration);
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && config.url !== '/auth/login') {
    config.headers.Authorization = `Bearer ${token}`;
    config.authGeneration = authGeneration;
  }
  return config;
});

// Handle 401 - redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401
      && error.config?.authGeneration !== undefined
      && clearAuthTokenForError(error)
    ) {
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) => {
  const formData = new FormData();
  formData.append('username', username);
  formData.append('password', password);
  return api.post('/auth/login', formData);
};

export const register = (userData) => api.post('/auth/register', userData);

// Users
export const getUsers = (params, config = {}) => api.get('/users', { ...config, params });
const getAllPages = async (request, params = {}, pageSize = 100, maxPages = 100, config = {}) => {
  const data = [];
  for (let page = 0; page < maxPages; page += 1) {
    const response = await request({ ...params, skip: page * pageSize, limit: pageSize }, config);
    const items = response.data || [];
    data.push(...items);
    if (items.length < pageSize) return { ...response, data };
  }
  throw new Error(`List exceeded the ${pageSize * maxPages} item safety limit`);
};
export const getAllUsers = (params) => getAllPages(getUsers, params);
export const getUser = (userId) => api.get(`/users/${userId}`);
export const getCurrentUser = () => api.get('/users/me');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (userId, data) => api.put(`/users/${userId}`, data);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);
export const getWorkerStats = (userId) => api.get(`/users/${userId}/stats`);

// Sofas
export const getSofas = (params, config = {}) => api.get('/sofas', { ...config, params });
export const getAllSofas = (params) => getAllPages(getSofas, params);
export const getSofa = (sofaId) => api.get(`/sofas/${sofaId}`);
export const createSofa = (data) => api.post('/sofas', data);
export const updateSofa = (sofaId, data) => api.put(`/sofas/${sofaId}`, data);
export const deleteSofa = (sofaId) => api.delete(`/sofas/${sofaId}`);
export const getNextSofaNumber = () => api.get('/sofas/next-number');
export const markSofaAsDefect = (sofaId, defectNotes) => 
  api.post(`/sofas/${sofaId}/defect`, null, { params: { defect_notes: defectNotes } });
export const assignWorkers = (sofaId, workerIds) => 
  api.post(`/sofas/${sofaId}/assign-workers`, null, { params: workerIds });
export const getArchivedSofas = (params) => api.get('/sofas/archive/', { params });
export const getAllArchivedSofas = (params) => getAllPages(getArchivedSofas, params, 500, 20);
export const getArchivedSofaDetails = (sofaId) => api.get(`/archive/${sofaId}`);
export const archiveSofa = (sofaId, reason) => 
  api.post(`/sofas/${sofaId}/archive`, null, { params: { reason } });
export const unarchiveSofa = (sofaId) => api.post(`/sofas/${sofaId}/unarchive`);
export const reorderSofa = (sofaId, direction) => 
  api.put('/sofas/reorder', { sofa_id: sofaId, direction });
export const reorderSofas = (order) => api.put('/sofas/reorder', order);

// Sofa Models
export const getSofaModels = (params, config = {}) => api.get('/sofa-models', { ...config, params });
export const getAllSofaModels = (params) => getAllPages(getSofaModels, params);
export const getSofaModel = (modelId) => api.get(`/sofa-models/${modelId}`);
export const createSofaModel = (data) => api.post('/sofa-models', data);
export const updateSofaModel = (modelId, data) => api.put(`/sofa-models/${modelId}`, data);
export const deleteSofaModel = (modelId) => api.delete(`/sofa-models/${modelId}`);
export const reorderSofaModels = (order) => api.put('/sofa-models/reorder', order);

// Tasks
export const getTasks = (params, config = {}) => api.get('/tasks', { ...config, params });
export const getAllTasks = (params) => getAllPages(getTasks, params);
export const getMyTasks = () => api.get('/tasks/my');
export const getTask = (taskId) => api.get(`/tasks/${taskId}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (taskId, data) => api.put(`/tasks/${taskId}`, data);
export const updateTaskStatus = (taskId, statusData) => api.put(`/tasks/${taskId}`, statusData);

// Materials
export const getFabrics = (params, config = {}) => api.get('/materials/fabrics', { ...config, params });
export const getAllFabrics = (params) => getAllPages(getFabrics, params);
export const createFabric = (data) => api.post('/materials/fabrics', data);
export const updateFabric = (fabricId, data) => api.put(`/materials/fabrics/${fabricId}`, data);
export const deleteFabric = (fabricId) => api.delete(`/materials/fabrics/${fabricId}`);
export const getMaterialMovements = (params) => api.get('/materials/movements', { params });
export const addMaterialMovement = (data) => api.post('/materials/movements', data);
export const getFabricStats = (fabricId) => api.get(`/materials/fabrics/${fabricId}/stats`);
export const getFabricUsage = (fabricId) => api.get(`/materials/fabrics/${fabricId}/usage`);

// Deliveries
export const getDeliveries = (params, config = {}) => api.get('/deliveries', { ...config, params });
export const getAllDeliveries = (params, config) => getAllPages(getDeliveries, params, 100, 100, config);
export const getDelivery = (deliveryId) => api.get(`/deliveries/${deliveryId}`);
export const createDelivery = (data) => api.post('/deliveries', data);
export const updateDelivery = (deliveryId, data) => api.put(`/deliveries/${deliveryId}`, data);
export const deleteDelivery = (deliveryId) => api.delete(`/deliveries/${deliveryId}`);
export const getReadyDeliveries = () => api.get('/deliveries/ready');
export const getMyDeliveries = (params) => api.get('/deliveries/my', { params });
export const getDeliveryStats = () => api.get('/deliveries/stats');
export const assignDriver = (deliveryId, data) => api.post(`/deliveries/${deliveryId}/assign`, data);
export const acceptDelivery = (deliveryId) => api.post(`/deliveries/${deliveryId}/accept`);
export const startDelivery = (deliveryId) => api.post(`/deliveries/${deliveryId}/start`);
export const arriveDelivery = (deliveryId) => api.post(`/deliveries/${deliveryId}/arrive`);
export const completeDelivery = (deliveryId, data) => api.post(`/deliveries/${deliveryId}/complete`, data);
export const reportDeliveryProblem = (deliveryId, data) => api.post(`/deliveries/${deliveryId}/problem`, data);
export const cancelDelivery = (deliveryId) => api.post(`/deliveries/${deliveryId}/cancel`);
export const getDeliveryHistory = (deliveryId) => api.get(`/deliveries/${deliveryId}/history`);
export const getDeliveryConfirmation = (deliveryId) => api.get(`/deliveries/${deliveryId}/confirmation`);

// Drivers
export const getDrivers = () => api.get('/users', { params: { role: 'driver' } });

// Notifications
export const getNotifications = (params, config = {}) => api.get('/notifications', { ...config, params });
export const getAllNotifications = (params, config) => getAllPages(getNotifications, params, 100, 10, config);
export const createNotification = (data) => api.post('/notifications', data);
export const markNotificationRead = (notificationId) => api.put(`/notifications/${notificationId}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const getUnreadCount = () => api.get('/notifications/unread/count');

// Analytics
export const getProductionStats = () => api.get('/analytics/production');
export const getWorkersStats = () => api.get('/analytics/workers');
export const getWorkerSalary = (workerId, periodDays) => api.get(`/analytics/salary/${workerId}`, { params: { period_days: periodDays } });
export const getDashboardStats = () => api.get('/analytics/dashboard');
export const globalSearch = (query, limit = 10) => api.get('/search', { params: { q: query, limit } });

export default api;
